import { useEffect, useState } from "react";
import { Loader2, AlertCircle, Info, Trophy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEAGUE = process.env.REACT_APP_WC_LEAGUE_ID || "1";
const SEASON = process.env.REACT_APP_WC_SEASON || "2022";
const IS_DEMO = process.env.REACT_APP_WC_IS_DEMO === "true";

const StatHead = ({ children, hideSm }) => (
  <th
    className={`px-2 py-2 text-center font-mono-data text-[10px] font-semibold uppercase tracking-wider text-brand-silver ${
      hideSm ? "hidden sm:table-cell" : ""
    }`}
  >
    {children}
  </th>
);

const GroupTable = ({ group }) => (
  <div
    data-testid={`standings-group-${group.name?.replace(/\s+/g, "-").toLowerCase()}`}
    className="overflow-hidden rounded-xl border border-brand-navy bg-brand-navy/30"
  >
    <div className="flex items-center justify-between border-b border-brand-navy bg-brand-navy/50 px-4 py-3">
      <h3 className="font-display text-base font-bold tracking-tight text-white">
        {group.name || "Group"}
      </h3>
      <Trophy className="h-4 w-4 text-brand-cyan/70" />
    </div>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-brand-navy/70">
            <StatHead>#</StatHead>
            <th className="px-2 py-2 text-left font-mono-data text-[10px] font-semibold uppercase tracking-wider text-brand-silver">
              Team
            </th>
            <StatHead>P</StatHead>
            <StatHead hideSm>W</StatHead>
            <StatHead hideSm>D</StatHead>
            <StatHead hideSm>L</StatHead>
            <StatHead hideSm>GF</StatHead>
            <StatHead hideSm>GA</StatHead>
            <StatHead>GD</StatHead>
            <StatHead>Pts</StatHead>
          </tr>
        </thead>
        <tbody>
          {group.rows.map((r, i) => {
            const qualifies = i < 2;
            return (
              <tr
                key={r.rank}
                className={`border-b border-brand-navy/40 transition-colors duration-200 hover:bg-brand-navy/40 ${
                  qualifies ? "bg-brand-cyan/[0.04]" : ""
                }`}
              >
                <td className="px-2 py-2.5 text-center">
                  <span
                    className={`inline-flex h-5 w-5 items-center justify-center rounded font-mono-data text-xs font-bold ${
                      qualifies ? "bg-brand-cyan/20 text-brand-cyan" : "text-brand-silver"
                    }`}
                  >
                    {r.rank}
                  </span>
                </td>
                <td className="px-2 py-2.5">
                  <div className="flex items-center gap-2.5">
                    {r.logo && (
                      <img
                        src={r.logo}
                        alt=""
                        className="h-5 w-5 shrink-0 rounded-sm object-contain"
                        loading="lazy"
                      />
                    )}
                    <span className="text-sm font-medium text-white">{r.team}</span>
                  </div>
                </td>
                <td className="px-2 py-2.5 text-center font-mono-data text-sm text-brand-silver">{r.played}</td>
                <td className="hidden px-2 py-2.5 text-center font-mono-data text-sm text-brand-silver sm:table-cell">{r.win}</td>
                <td className="hidden px-2 py-2.5 text-center font-mono-data text-sm text-brand-silver sm:table-cell">{r.draw}</td>
                <td className="hidden px-2 py-2.5 text-center font-mono-data text-sm text-brand-silver sm:table-cell">{r.lose}</td>
                <td className="hidden px-2 py-2.5 text-center font-mono-data text-sm text-brand-silver sm:table-cell">{r.gf}</td>
                <td className="hidden px-2 py-2.5 text-center font-mono-data text-sm text-brand-silver sm:table-cell">{r.ga}</td>
                <td className="px-2 py-2.5 text-center font-mono-data text-sm text-brand-silver">
                  {r.goalsDiff > 0 ? `+${r.goalsDiff}` : r.goalsDiff}
                </td>
                <td className="px-2 py-2.5 text-center font-mono-data text-sm font-bold text-white">{r.points}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

/**
 * Custom Standings widget — fetches normalized standings from our backend
 * proxy (/api/standings), which calls API-Sports server-side (key stays
 * server-side) and caches the response. Renders brand-styled group tables.
 */
export const StandingsWidget = ({ league = LEAGUE, season = SEASON }) => {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let active = true;
    setState({ loading: true, error: null, data: null });
    fetch(`${API}/standings?league=${league}&season=${season}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (active) setState({ loading: false, error: null, data });
      })
      .catch((e) => {
        if (active) setState({ loading: false, error: e.message, data: null });
      });
    return () => {
      active = false;
    };
  }, [league, season]);

  const { loading, error, data } = state;
  const groups = data?.groups || [];
  const noData = !loading && !error && groups.length === 0;

  return (
    <div data-testid="standings-widget" className="w-full">
      {IS_DEMO && (
        <div
          data-testid="standings-demo-note"
          className="mb-5 flex items-start gap-3 rounded-xl border border-brand-cyan/30 bg-brand-cyan/10 px-5 py-3.5 text-brand-cyan"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p className="text-xs leading-relaxed">
            Showing the <strong>2022 World Cup</strong> as a live demo — the current
            API-Sports plan doesn&apos;t include the 2026 season yet. After upgrading,
            set <code className="font-mono-data">REACT_APP_WC_SEASON=2026</code> &amp;{" "}
            <code className="font-mono-data">REACT_APP_WC_IS_DEMO=false</code>.
          </p>
        </div>
      )}

      {loading && (
        <div
          data-testid="standings-loading"
          className="flex items-center justify-center gap-3 rounded-2xl border border-brand-navy bg-brand-navy/20 py-20 text-brand-silver"
        >
          <Loader2 className="h-5 w-5 animate-spin text-brand-cyan" />
          <span className="text-sm font-medium">Loading live standings…</span>
        </div>
      )}

      {error && (
        <div
          data-testid="standings-error"
          className="flex items-center justify-center gap-3 rounded-2xl border border-red-500/30 bg-red-500/10 py-16 text-red-300"
        >
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm font-medium">Couldn&apos;t load standings. Please try again later.</span>
        </div>
      )}

      {noData && (
        <div
          data-testid="standings-empty"
          className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-brand-navy bg-brand-navy/20 py-16 text-center"
        >
          <Trophy className="h-7 w-7 text-brand-silver" />
          <p className="text-sm font-medium text-white">Standings not available yet</p>
          <p className="max-w-sm text-xs text-brand-silver">
            Tables will populate once group-stage matches are played for this season.
          </p>
        </div>
      )}

      {!loading && !error && groups.length > 0 && (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {groups.map((g, i) => (
            <GroupTable key={g.name || i} group={g} />
          ))}
        </div>
      )}
    </div>
  );
};

export default StandingsWidget;
